// BreakdownGrid — 견적 상세 항목을 고정 9열 표로 렌더 (내용추가17, C안).
//
// 열: 도면 | 품목 정보 | 재료비 | 가공비 | 기타비용 | 내부단가 | 견적가 | 이윤 | 이익률 — 항상 9개.
// 재료비 세부·공정은 가로 열이 아니라 그룹 셀 안 세로 목록으로 쌓이므로 항목이 늘어도 폭이 늘지 않는다
// (기존 분해표는 22열 → 인쇄 시 셀이 좁아져 글자 단위로 꺾였다). 값은 costGroups 픽만 — 계산 없음.
//
// 인쇄: <thead> 에 기본정보 밴드(인쇄 전용)·섹션 제목·열 머리글을 두어 매 페이지 상단에 반복하고,
// 품목 행(<tr>)은 페이지 사이에서 잘리지 않는다(DetailScreen 인쇄 CSS).

import { type BreakdownRow, type CostGroup, costGroups } from "@/lib/cost/cncBreakdown";
import { MARGIN_FLOOR, fmt, fmt1 } from "@/lib/data";
import type { CSSProperties, ReactNode } from "react";

const dash = (n: number): string => (n ? fmt(n) : "-");
const dash1 = (n: number): string => (n ? fmt1(n) : "-");

const num: CSSProperties = { fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };
const CELL_RULE = "1px solid var(--table-border)";
const ITEM_RULE = "2px solid var(--table-border-strong)";

// 열 너비(%) — 도면·품목 정보·그룹 3열이 폭 대부분, 단가 4열은 숫자 한 줄 폭.
const COLS: [string, number][] = [
  ["img", 7],
  ["item", 20],
  ["material", 16],
  ["processing", 18],
  ["other", 13],
  ["internal", 6.5],
  ["supply", 6.5],
  ["profit", 6.5],
  ["rate", 6.5],
];

const th: CSSProperties = {
  padding: "10px 12px",
  fontWeight: 800,
  fontSize: "0.93em",
  color: "var(--text-dim)",
  textAlign: "center",
  whiteSpace: "nowrap",
  background: "var(--bg-card-hover)",
  border: CELL_RULE,
};
const td: CSSProperties = {
  padding: "12px 14px",
  verticalAlign: "top",
  border: CELL_RULE,
  borderTop: ITEM_RULE,
};
const priceTd: CSSProperties = {
  ...td,
  ...num,
  textAlign: "right",
  verticalAlign: "middle",
  color: "var(--text-base)",
};

// 그룹 셀 — 세부 줄 목록 + 구분선 + 계. 계는 셀 바닥에 붙여 같은 행의 세 그룹이 한 줄에 맞도록
// (td height:1px + 내부 height:100% — 행 높이를 채우는 표준 우회).
const GroupCell = ({ group }: { group: CostGroup }) => (
  <td className="qd-groupcell" style={{ ...td, height: 1 }}>
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {group.lines.length > 0 ? (
        group.lines.map((l) => (
          <div
            key={l.name}
            className="qd-line"
            style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "2px 0" }}
          >
            <span style={{ color: "var(--text-near)" }}>{l.name}</span>
            <span style={{ ...num, color: "var(--text-base)" }}>{dash1(l.cost)}</span>
          </div>
        ))
      ) : (
        <div style={{ color: "var(--text-faint)", padding: "2px 0" }}>-</div>
      )}
      <div
        className="qd-celltotal"
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 10,
          marginTop: "auto",
          paddingTop: 8,
          borderTop: "1px solid var(--table-border-strong)",
          fontWeight: 800,
          color: "var(--text-base)",
        }}
      >
        <span>계</span>
        <span style={num}>{dash1(group.total)}</span>
      </div>
    </div>
  </td>
);

export const BreakdownGrid = ({
  rows,
  procOrder,
  head,
  band,
  thumb,
  spec,
  scale,
  bold,
}: {
  rows: BreakdownRow[];
  procOrder: readonly string[]; // 원가마스터 공정 노출순서 — 가공비 줄 정렬 기준
  head: ReactNode; // 섹션 제목 + 화면 컨트롤 — thead 에 두어 인쇄 시 매 페이지 반복
  band: ReactNode; // 인쇄 시 매 페이지 상단에 반복할 기본정보 밴드(화면에선 숨김)
  thumb: (i: number) => ReactNode;
  spec: (i: number) => ReactNode;
  scale: number; // 글자배율(A−/A+) — 화면 글자 크기, 인쇄는 --qd-scale 로 반영
  bold: boolean;
}) => (
  <div
    className="qd-gridwrap"
    style={
      {
        "--qd-scale": scale,
        fontSize: 15 * scale,
        fontWeight: bold ? 700 : 400,
        overflowX: "auto",
      } as CSSProperties
    }
  >
    <table
      className="qd-grid"
      style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}
    >
      <colgroup>
        {COLS.map(([key, w]) => (
          <col key={key} style={{ width: `${w}%` }} />
        ))}
      </colgroup>
      <thead>
        <tr className="qd-bandrow">
          <td colSpan={9}>{band}</td>
        </tr>
        <tr className="qd-headrow">
          <td colSpan={9}>{head}</td>
        </tr>
        <tr>
          <th style={th}>도면</th>
          <th style={{ ...th, textAlign: "left" }}>품목 정보</th>
          <th style={th}>재료비</th>
          <th style={th}>가공비</th>
          <th style={th}>기타비용</th>
          <th style={th}>내부단가</th>
          <th style={{ ...th, color: "var(--brand)" }}>견적가</th>
          <th style={th}>이윤</th>
          <th style={th}>이익률</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => {
          const [mat, proc, other] = costGroups(r, procOrder);
          const low = r.profitRate < MARGIN_FLOOR;
          return (
            <tr key={`${r.partNo}-${i}`} className="qd-item">
              {/* 도면 칸은 좌우 여백을 줄여 고정 열 폭 대부분을 이미지에 쓴다 */}
              <td className="qd-imgcell" style={{ ...td, padding: "10px 6px", textAlign: "center" }}>
                {thumb(i)}
              </td>
              <td style={td}>
                <div
                  className="qd-itemname"
                  style={{
                    fontWeight: 800,
                    color: "var(--text-base)",
                    whiteSpace: "pre-line",
                    overflowWrap: "anywhere",
                  }}
                >
                  {i + 1}. {r.itemName?.trim() || r.partNo || "-"}
                </div>
                <div
                  className="qd-iteminfo"
                  style={{
                    marginTop: 6,
                    fontSize: "0.87em",
                    color: "var(--text-dim)",
                    lineHeight: 1.6,
                  }}
                >
                  <div>
                    {r.quoteTypeLabel || "-"} · {spec(i)}
                  </div>
                  <div>
                    {r.material || "-"}
                    {r.productWeightG > 0 && ` · ${r.productWeightG.toLocaleString("ko-KR")}g`}
                  </div>
                  {r.avgVolume > 0 && <div>연평균 {fmt(r.avgVolume)} EA</div>}
                </div>
              </td>
              <GroupCell group={mat} />
              <GroupCell group={proc} />
              <GroupCell group={other} />
              <td style={priceTd}>{dash1(r.internalCost)}</td>
              <td style={{ ...priceTd, fontWeight: 800 }}>{dash(r.supplyPrice)}</td>
              <td style={priceTd}>{dash(r.profit)}</td>
              <td
                style={{
                  ...priceTd,
                  color: low ? "var(--text-negative)" : "var(--text-near)",
                  fontWeight: low ? 800 : undefined,
                }}
              >
                {r.profitRate.toFixed(1)}%
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);
